import React, { useEffect, useState } from 'react';
import { Progress, Typography, Space, theme } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const { Text } = Typography;

interface PasswordStrengthMeterProps {
  password: string;
  onStrengthChange?: (score: number) => void;
}

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  onStrengthChange
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState<string>('');
  const [color, setColor] = useState<string>(token.colorError);

  const requirements: PasswordRequirement[] = [
    { label: translate(t, TRANSLATIONS.PASSWORD_STRENGTH_REQ_LENGTH), test: (pwd) => pwd.length >= 8 },
    { label: translate(t, TRANSLATIONS.PASSWORD_STRENGTH_REQ_UPPERCASE), test: (pwd) => /[A-Z]/.test(pwd) },
    { label: translate(t, TRANSLATIONS.PASSWORD_STRENGTH_REQ_LOWERCASE), test: (pwd) => /[a-z]/.test(pwd) },
    { label: translate(t, TRANSLATIONS.PASSWORD_STRENGTH_REQ_NUMBER), test: (pwd) => /\d/.test(pwd) },
    { label: translate(t, TRANSLATIONS.PASSWORD_STRENGTH_REQ_SPECIAL), test: (pwd) => /[@$!%*?&]/.test(pwd) },
  ];

  useEffect(() => {
    if (!password) {
      setScore(0);
      setFeedback('');
      setColor(token.colorError);
      onStrengthChange?.(0);
      return;
    }

    const metRequirements = requirements.filter(req => req.test(password)).length;
    const calculatedScore = (metRequirements / requirements.length) * 100;

    setScore(calculatedScore);
    onStrengthChange?.(calculatedScore);

    if (calculatedScore < 40) {
      setFeedback(translate(t, TRANSLATIONS.PASSWORD_STRENGTH_WEAK));
      setColor(token.colorError);
    } else if (calculatedScore < 60) {
      setFeedback(translate(t, TRANSLATIONS.PASSWORD_STRENGTH_FAIR));
      setColor(token.colorWarning);
    } else if (calculatedScore < 80) {
      setFeedback(translate(t, TRANSLATIONS.PASSWORD_STRENGTH_GOOD));
      setColor('#1890ff');
    } else {
      setFeedback(translate(t, TRANSLATIONS.PASSWORD_STRENGTH_STRONG));
      setColor(token.colorSuccess);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, onStrengthChange, t]);

  if (!password) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <Progress 
        percent={score} 
        strokeColor={color}
        showInfo={false}
        size="small"
      />
      <div style={{ marginTop: 8, marginBottom: 12 }}>
        <Text style={{ color: color, fontWeight: 500 }}>
          {feedback}
        </Text>
      </div>
      
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        {requirements.map((req, index) => {
          const isMet = req.test(password);
          return (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isMet ? (
                <CheckCircleOutlined style={{ color: token.colorSuccess, fontSize: 14 }} />
              ) : (
                <CloseCircleOutlined style={{ color: '#d9d9d9', fontSize: 14 }} />
              )}
              <Text style={{ fontSize: 13, color: isMet ? token.colorSuccess : token.colorTextTertiary }}>
                {req.label}
              </Text>
            </div>
          );
        })}
      </Space>
    </div>
  );
};

export default PasswordStrengthMeter;